import type { AgentContext } from '../resources/types'
import type { SandboxConfig } from './sandboxConfigSchema'

export const LEGACY_DEFAULT_SANDBOX_SCENARIO_ID = 'default'

export type SandboxScenarioListItem = {
  id: string
  label: string
}

function legacyContextFromSandbox(
  sandbox: SandboxConfig | undefined,
): AgentContext | undefined {
  if (!sandbox) return undefined
  if (sandbox.context) return sandbox.context
  if (sandbox.mockContext) return sandbox.mockContext
  return undefined
}

export function listSandboxScenarios(
  sandbox: SandboxConfig | undefined,
): SandboxScenarioListItem[] {
  if (!sandbox) return []

  const entries = sandbox.scenarios
    ? Object.entries(sandbox.scenarios)
    : null

  if (entries && entries.length > 0) {
    return entries.map(([id, scenario]) => ({
      id,
      label: scenario.label?.trim() || id,
    }))
  }

  const legacy = legacyContextFromSandbox(sandbox)
  if (legacy) {
    return [{ id: LEGACY_DEFAULT_SANDBOX_SCENARIO_ID, label: 'Default' }]
  }

  return []
}

export function resolveDefaultSandboxScenarioId(
  sandbox: SandboxConfig | undefined,
): string | undefined {
  const scenarios = listSandboxScenarios(sandbox)
  if (scenarios.length === 0) return undefined

  const preferred = sandbox?.defaultScenario?.trim()
  if (preferred && scenarios.some((s) => s.id === preferred)) {
    return preferred
  }

  return scenarios[0]?.id
}

export function resolveSandboxScenarioContext(
  sandbox: SandboxConfig | undefined,
  scenarioId?: string | null,
): AgentContext | undefined {
  if (!sandbox) return undefined

  const scenarios = sandbox.scenarios
  if (scenarios && Object.keys(scenarios).length > 0) {
    const id =
      scenarioId?.trim() ||
      resolveDefaultSandboxScenarioId(sandbox) ||
      Object.keys(scenarios)[0]
    const scenario = id ? scenarios[id] : undefined
    if (scenario?.context) return scenario.context
    const firstKey = Object.keys(scenarios)[0]
    return firstKey ? scenarios[firstKey]?.context : undefined
  }

  return legacyContextFromSandbox(sandbox)
}

export function listSandboxScenariosWithContext(
  sandbox: SandboxConfig | undefined,
): Array<SandboxScenarioListItem & { context: AgentContext }> {
  if (!sandbox) return []

  const entries = sandbox.scenarios
    ? Object.entries(sandbox.scenarios)
    : null

  if (entries && entries.length > 0) {
    return entries.map(([id, scenario]) => ({
      id,
      label: scenario.label?.trim() || id,
      context: scenario.context,
    }))
  }

  const legacy = legacyContextFromSandbox(sandbox)
  if (legacy) {
    return [
      {
        id: LEGACY_DEFAULT_SANDBOX_SCENARIO_ID,
        label: 'Default',
        context: legacy,
      },
    ]
  }

  return []
}
